"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";

// Depth layers, back → front.
//   scroll = how much it lags the page scroll (bigger = further away)
//   mouse  = how much it drifts with the cursor (bigger = nearer)
type Layer = {
  src: string;
  scroll: number;
  mouse: number;
  anim?: string;
  fit?: string;
  place?: boolean;
};

// Colored Dilijan landmark strip (same for day & night). Rendered small and
// pinned bottom-right. Currently hidden — add PLACES back to the layer arrays
// below to re-enable it.
// const PLACES: Layer = {
//   src: "/images/hero-places.svg",
//   scroll: 0.05,
//   mouse: 0.8,
//   place: true,
// };

const NIGHT_LAYERS: Layer[] = [
  { src: "/images/hero-sky.svg", scroll: 0.1, mouse: 0.3, anim: "animate-hero-zoom" },
  { src: "/images/hero-back.svg", scroll: 0.06, mouse: 0.6, anim: "hero-float-back" },
  { src: "/images/hero-front.svg", scroll: 0.02, mouse: 1, anim: "hero-float-front" },
];

const DAY_LAYERS: Layer[] = [
  { src: "/images/hero-sky-day.svg", scroll: 0.1, mouse: 0.3, anim: "animate-hero-zoom" },
  { src: "/images/hero-back-day.svg", scroll: 0.06, mouse: 0.6, anim: "hero-float-back" },
  { src: "/images/hero-front-day.svg", scroll: 0.02, mouse: 1, anim: "hero-float-front" },
];

const MOUSE_AMP = 32; // px of cursor drift at the edges

export function HeroParallax({
  night = false,
  placesVersion,
}: {
  night?: boolean;
  placesVersion?: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const LAYERS = night ? NIGHT_LAYERS : DAY_LAYERS;

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const layers = Array.from(
      el.querySelectorAll<HTMLElement>("[data-scroll]"),
    );
    let raf = 0;
    let mx = 0;

    const apply = () => {
      const y = window.scrollY;
      for (const l of layers) {
        const s = Number(l.dataset.scroll);
        const m = Number(l.dataset.mouse);
        // Horizontal drift from the cursor; vertical only from scroll. The
        // mountain layers are bottom-anchored, so vertical mouse drift would
        // lift them and expose the layers behind at the bottom.
        const tx = (mx * MOUSE_AMP * m).toFixed(1);
        const ty = (y * s).toFixed(1);
        l.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      }
      raf = 0;
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };

    const onMove = (e: PointerEvent) => {
      mx = e.clientX / window.innerWidth - 0.5;
      schedule();
    };

    apply();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("pointermove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={root}
      aria-hidden
      className="absolute inset-0 -z-10 overflow-hidden"
    >
      {LAYERS.map((l) =>
        l.place ? (
          <div
            key={l.src}
            data-scroll={l.scroll}
            data-mouse={l.mouse}
            className="absolute inset-0 will-change-transform"
          >
            {/* Bottom-right with a margin from the edges; gentle zoom. */}
            <div className="hero-zoom-soft absolute bottom-6 right-4 aspect-[2814/408] w-[60%] sm:bottom-10 sm:right-10 sm:w-[53%]">
              <Image
                src={placesVersion ? `${l.src}?v=${placesVersion}` : l.src}
                alt=""
                fill
                priority
                unoptimized
                sizes="60vw"
                className="object-contain object-bottom opacity-75"
              />
            </div>
          </div>
        ) : (
          <div
            key={l.src}
            data-scroll={l.scroll}
            data-mouse={l.mouse}
            className="absolute inset-0 will-change-transform"
          >
            <div className={`relative h-full w-full ${l.anim ?? ""}`}>
              <Image
                src={l.src}
                alt=""
                fill
                priority
                sizes="100vw"
                className={l.fit ?? "object-cover object-bottom"}
              />
            </div>
          </div>
        ),
      )}
    </div>
  );
}
